<template>
  <!-- A story screen (components/story/screens/*) on the feature stage backdrop, the same dot grid
       and plan glow as StoryChatStage. With `autoplay` (the hero) it plays the screen's states once
       when it scrolls into view, then rests on the last with a Replay button (under 10 s, WCAG
       2.2.2). Without `autoplay`, `state` drives it (the sticky chapters). The server and
       reduce-motion render the last state, the payoff. -->
  <div ref="rootEl" class="sst sst--screen" :class="{ 'sst--tall': tall }">
    <div class="sst-frame" role="img" translate="no" :aria-label="label || undefined">
      <component :is="screens[screen]" :state="current" />
    </div>
    <button v-show="canReplay" type="button" class="sst-replay" aria-label="Replay preview" title="Replay preview" @click="play">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" /></svg>
    </button>
  </div>
</template>

<script setup>
import { defineAsyncComponent } from "vue";

const props = defineProps({
  // Key in `screens` below.
  screen: { type: String, required: true },
  // The screen's states in play order; the last is the resting frame.
  states: { type: Array, default: () => [] },
  // How long each state holds while playing (ms).
  hold: { type: Number, default: 2000 },
  autoplay: { type: Boolean, default: false },
  state: { type: String, default: "" },
  tall: { type: Boolean, default: false },
  // What the replica shows, for screen readers (the window itself is aria-hidden).
  label: { type: String, default: "" },
});

// Each screen loads in its own chunk; the server still renders it.
const screens = {
  dashboard: defineAsyncComponent(() => import("./screens/DashboardScreen.vue")),
  timer: defineAsyncComponent(() => import("./screens/TimerScreen.vue")),
  timesheet: defineAsyncComponent(() => import("./screens/TimesheetScreen.vue")),
  expenses: defineAsyncComponent(() => import("./screens/ExpensesScreen.vue")),
  accounting: defineAsyncComponent(() => import("./screens/AccountingScreen.vue")),
  calendar: defineAsyncComponent(() => import("./screens/CalendarScreen.vue")),
  projects: defineAsyncComponent(() => import("./screens/ProjectsScreen.vue")),
  mobile: defineAsyncComponent(() => import("./screens/MobileScreen.vue")),
  changeorder: defineAsyncComponent(() => import("./screens/ChangeOrderScreen.vue")),
  files: defineAsyncComponent(() => import("./screens/FilesScreen.vue")),
  products: defineAsyncComponent(() => import("./screens/ProductsScreen.vue")),
  support: defineAsyncComponent(() => import("./screens/SupportScreen.vue")),
  appearance: defineAsyncComponent(() => import("./screens/AppearanceScreen.vue")),
  brand: defineAsyncComponent(() => import("./screens/BrandScreen.vue")),
  pdf: defineAsyncComponent(() => import("./screens/PdfScreen.vue")),
  currency: defineAsyncComponent(() => import("./screens/CurrencyScreen.vue")),
  today: defineAsyncComponent(() => import("./screens/TodayScreen.vue")),
};

// The hero (autoplay) is above the fold, so a screen may fetch its front image at high priority.
provide("storyStageHero", props.autoplay);

const rootEl = ref(null);
const playing = ref(null);
const resting = ref(false);
let timers = [];
let observer = null;

const last = computed(() => props.states[props.states.length - 1] || props.state);
const current = computed(() => (props.autoplay ? playing.value ?? last.value : props.state || last.value));
const canReplay = computed(() => props.autoplay && resting.value);

const clear = () => {
  timers.forEach(clearTimeout);
  timers = [];
};
const play = () => {
  clear();
  resting.value = false;
  props.states.forEach((step, index) => {
    timers.push(setTimeout(() => { playing.value = step; }, index * props.hold));
  });
  timers.push(setTimeout(() => { resting.value = true; }, (props.states.length - 1) * props.hold + 900));
};

onMounted(() => {
  if (!props.autoplay || props.states.length < 2) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  observer = new IntersectionObserver(([entry]) => {
    if (!entry.isIntersecting) return;
    observer.disconnect();
    play();
  }, { threshold: 0.35 });
  observer.observe(rootEl.value);
});

onBeforeUnmount(() => {
  clear();
  observer?.disconnect();
});
</script>

<style>
.sst {
  --sst-p1: 124 155 224;
  --sst-p2: 159 139 230;
  --sst-dot: rgb(0 0 0 / 0.1);
  position: relative;
  height: 470px;
  border-radius: 14px;
  background-color: rgb(0 0 0 / 0.018);
  isolation: isolate;
}
.sst--tall { height: 520px; }
.sst::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  background:
    radial-gradient(circle, var(--sst-dot) 0.8px, transparent 1.3px) 7px 7px / 14px 14px,
    radial-gradient(150% 150% at 50% 45%, rgb(var(--sst-p1) / 0.16) 0%, rgb(var(--sst-p2) / 0.08) 50%, rgb(var(--sst-p2) / 0) 100%);
  -webkit-mask-image: radial-gradient(closest-side, #000 70%, rgb(0 0 0 / 0.55) 100%);
  mask-image: radial-gradient(closest-side, #000 70%, rgb(0 0 0 / 0.55) 100%);
}
.dark .sst { --sst-dot: rgb(255 255 255 / 0.13); background-color: rgb(255 255 255 / 0.03); }
.sst-frame { height: 100%; max-width: 520px; margin: 0 auto; padding: 28px 28px 30px; }
/* StoryScene and StoryReplicaStage style the same unscoped .sst-frame at 520px, so a bare 540px
   here won or lost by stylesheet order (and leaked onto their frames). Scoped to this stage. */
.sst--screen > .sst-frame { max-width: 540px; }
.sst-replay {
  position: absolute;
  right: 8px;
  bottom: 4px;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #595f68;
  cursor: pointer;
}
.dark .sst-replay { color: #a3a3a3; }
.sst-replay:hover { background: rgb(0 0 0 / 0.08); }
.sst-replay:focus-visible { outline: 2px solid #0e7490; outline-offset: 1px; }
.sst-replay svg { width: 20px; height: 20px; fill: currentColor; }
@media (max-width: 639px) {
  .sst, .sst--tall { height: 500px; }
  .sst-frame { padding: 14px 12px 44px; }
}
</style>
